import { translate } from 'helpers/translateHelper';
import { filterKeys } from './constants';

export const updateCurrentFleetsOnFilterChange = (fleetsFilter, state) => {
    const currentfleets = state?.assetGroups?.currentData ?? [];
    const fleets = state?.assetGroups?.data ?? [];
    // Use either the provided `fleets` (if caller passed full list) or `currentfleets` as the data to filter.
    let fleetsToFilter = [];
    if (fleets?.length > 0) {
        fleetsToFilter = fleets;
    } else if (currentfleets?.length > 0) {
        fleetsToFilter = currentfleets;
    }
    if (!fleetsToFilter.length) { return []; }

    const activeFilters = { ...fleetsFilter };

    const normalizeArray = (val) => {
        if (!val || val?.length === 0) { return null; }
        if (Array.isArray(val) && val.length > 0) { return val?.filter(v => v !== null).map(v => v); }
        return [val];
    };

    const siteIds = normalizeArray(activeFilters.siteId);
    const countryCodes = normalizeArray(activeFilters.countryCode);
    const organizationIds = normalizeArray(activeFilters.organizationId);
    const owners = normalizeArray(activeFilters.owner);

    const getSites = (fleet) => {
        return fleet.sites || [];
    };

    let filtered = fleetsToFilter;

    if (siteIds && siteIds.length > 0) {
        filtered = filtered.filter(fleet => {
            const sites = getSites(fleet);
            const matchedInSites = sites.some(site => {
                const id = site?.siteId ?? site?.id ?? null;
                return id !== null && siteIds.includes(id);
            });
            if (matchedInSites) { return true; }
            const fleetSiteId = fleet?.siteId ?? fleet?.site?.siteId ?? null;
            return fleetSiteId !== null && siteIds.includes(fleetSiteId);
        });
    }

    if (countryCodes && countryCodes.length > 0) {
        filtered = filtered.filter(fleet => {
            const sites = getSites(fleet);
            const matched = sites.some(site => {
                const key = site?.countryCode ?? site?.country ?? site?.countryName ?? null;
                return key !== null && countryCodes.includes(String(key));
            });
            if (matched) { return true; }
            const fleetCountryKey = fleet?.countryCode ?? fleet?.country ?? null;
            return fleetCountryKey !== null && countryCodes.includes(String(fleetCountryKey));
        });
    }

    if (organizationIds && organizationIds.length > 0) {
        filtered = filtered.filter(fleet => fleet?.organizationData?.id && organizationIds.includes(fleet.organizationData.id));
    }

    if (owners && owners.length > 0) {
        filtered = filtered.filter(fleet => {
            const o = fleet?.fleetData?.owner ?? fleet?.owner ?? null;
            return o !== null && owners?.includes(o);
        });
    }

    return filtered || [];
};


const addSite = (site, siteMap) => {
    if (!site) { return; }
    const siteId = site.siteId ?? null;
    const siteName = site.name ?? '';
    if (siteId !== null && !siteMap.some(s => s?.value === siteId)) {
        siteMap.push({ value: siteId, label: siteName });
    }
};

const addCountry = (site, countryMap) => {
    const country = site.country;
    const countryCode = site.countryCode ?? null;
    if (countryCode && !countryMap.some(c => c.value === countryCode)) {
        countryMap.push({ value: countryCode, label: country });
    }
};

const addOrganisation = (orgData, orgMap) => {
    if (orgData) {
        const orgId = orgData.id ?? null;
        if (orgId !== null && !orgMap.some(o => o.value === orgId)) {
            const orgName = orgData.name ?? '';
            orgMap.push({ value: orgId, label: orgName });
        }
    }
};

const addOwner = (owner, ownerMap) => {
    if (owner && !ownerMap.some(o => o.value === owner)) {
        ownerMap.push({ value: owner, label: owner });
    }
};

const getRemainingFleets = (allFleets, assetGroupsData) => {
    return (allFleets ?? []).filter(fleet => (assetGroupsData ?? []).every(ag => ag?.id !== fleet?.id));
};

const hasActiveFilterValue = (value) => {
    if (Array.isArray(value)) {
        return value.some(item => item !== null && item !== undefined && item !== '');
    }
    return value !== null && value !== undefined && value !== '';
};

const getSingleActiveFilterKey = (activeFilters = {}) => {
    const filterKeysArray = Object.values(filterKeys);

    const activeKeys = filterKeysArray?.filter(key => hasActiveFilterValue(activeFilters?.[key]));
    return activeKeys?.length === 1 ? activeKeys[0] : null;
};

const formatAllOptionsWithoutClearSection = (options) => {
    return [{ label: null, options }];
};

const formatAllOptionsInClearSection = (options) => {
    if (!options || options.length === 0) {
        return [];
    }

    return [{
        label: translate('ABB.Powertrain.Frontend.fleetClearsOtherFiltersLabel')?.toUpperCase(),
        options: options.map(option => ({ ...option, clearFilters: true }))
    }];
};

const collectAllSites = (fleets) => {
    const sites = [];
    (fleets ?? []).forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(site => {
                addSite(site, sites);
            });
        }
    });
    return sites;
};

const collectAllCountries = (fleets) => {
    const countries = [];
    (fleets ?? []).forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(site => {
                addCountry(site, countries);
            });
        }
    });
    return countries;
};

const collectAllOrganisations = (fleets) => {
    const organisations = [];
    (fleets ?? []).forEach((group) => {
        if (group?.organizationData) {
            addOrganisation(group.organizationData, organisations);
        }
    });
    return organisations;
};

const collectAllOwners = (fleets) => {
    const owners = [];
    (fleets ?? []).forEach((group) => {
        if (group?.fleetData?.owner) {
            addOwner(group.fleetData.owner, owners);
        }
    });
    return owners;
};

const mapToFormattedOptions = (current, remaining, options) => {
    return options.push({
        label: null,
        options: current
    }, {
        label: translate('ABB.Powertrain.Frontend.fleetClearsOtherFiltersLabel')?.toUpperCase(),
        options: remaining.map((opt) => ({ ...opt, clearFilters: true })).filter(opt => !current.some(cs => cs?.value === opt?.value))
    });
};


/* UPDATE SITE FILTER OPTIONS BASED ON ANY FILTER SELECTION */
const createGroupforSites = (allFleets, options, assetGroupsData, singleActiveFilterKey) => {
    const updatedOptions = [], currentSites = [], remainingSites = [];
    const remainingFleets = getRemainingFleets(allFleets, assetGroupsData);
    if (!assetGroupsData || assetGroupsData?.length === 0) {
        return formatAllOptionsInClearSection(collectAllSites(allFleets));
    }
    if (!options || options.length === 0) {
        return updatedOptions;
    }
    if (singleActiveFilterKey === filterKeys.siteId) {
        return formatAllOptionsWithoutClearSection(collectAllSites(allFleets));
    }
    remainingFleets.forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(s => {
                addSite(s, remainingSites);
            });
        }
    });
    assetGroupsData.forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(s => {
                addSite(s, currentSites);
            });
        }
    });
    mapToFormattedOptions(currentSites, remainingSites, updatedOptions);
    return updatedOptions;
};

const createGroupforCountry = (allFleets, options, assetGroupsData, singleActiveFilterKey) => {
    const updatedOptions = [], currentCountries = [], remainingCountries = [];
    const remainingFleets = getRemainingFleets(allFleets, assetGroupsData);
    if (!assetGroupsData || assetGroupsData?.length === 0) {
        return formatAllOptionsInClearSection(collectAllCountries(allFleets));
    }
    if (!options || options.length === 0) {
        return updatedOptions;
    }
    if (singleActiveFilterKey === filterKeys.countryCode) {
        return formatAllOptionsWithoutClearSection(collectAllCountries(allFleets));
    }
    remainingFleets.forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(s => {
                addCountry(s, remainingCountries);
            });
        }
    });
    assetGroupsData.forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(s => {
                addCountry(s, currentCountries);
            });
        }
    });
    mapToFormattedOptions(currentCountries, remainingCountries, updatedOptions);
    return updatedOptions;
};

const createGroupforOrganisation = (allFleets, options, assetGroupsData, singleActiveFilterKey) => {
    const updatedOptions = [], currentOrganisations = [], remainingOrganisations = [];
    const remainingFleets = getRemainingFleets(allFleets, assetGroupsData);

    if (!assetGroupsData || assetGroupsData?.length === 0) {
        return formatAllOptionsInClearSection(collectAllOrganisations(allFleets));
    }
    if (!options || options.length === 0) {
        return updatedOptions;
    }
    if (singleActiveFilterKey === filterKeys.organizationId) {
        return formatAllOptionsWithoutClearSection(collectAllOrganisations(allFleets));
    }
    remainingFleets.forEach((group) => {
        if (group?.organizationData) {
            addOrganisation(group.organizationData, remainingOrganisations);
        }
    });
    assetGroupsData.forEach((group) => {
        if (group?.organizationData) {
            addOrganisation(group.organizationData, currentOrganisations);
        }
    });
    mapToFormattedOptions(currentOrganisations, remainingOrganisations, updatedOptions);
    return updatedOptions;
};

const createGroupforOwner = (allFleets, options, assetGroupsData, singleActiveFilterKey) => {
    const updatedOptions = [], currentOwners = [], remainingOwners = [];
    const remainingFleets = getRemainingFleets(allFleets, assetGroupsData);
    if (!assetGroupsData || assetGroupsData?.length === 0) {
        return formatAllOptionsInClearSection(collectAllOwners(allFleets));
    }
    if (!options || options.length === 0) {
        return updatedOptions;
    }
    if (singleActiveFilterKey === filterKeys.owner) {
        return formatAllOptionsWithoutClearSection(collectAllOwners(allFleets));
    }
    remainingFleets.forEach((group) => {
        if (group?.fleetData?.owner) {
            addOwner(group.fleetData.owner, remainingOwners);
        }
    });
    assetGroupsData.forEach((group) => {
        if (group?.fleetData?.owner) {
            addOwner(group.fleetData.owner, currentOwners);
        }
    });
    mapToFormattedOptions(currentOwners, remainingOwners, updatedOptions);
    return updatedOptions;
};

export const extractSitesAndCountries = (state, assetGroupsData, activeFilters = {}) => {
    const allFleets = state?.assetGroups?.data ?? [];
    if (allFleets.length === 0) {
        return { allFleetSites: [], allFleetCountries: [], allFleetOrganizations: [], allFleetOwners: [] };
    }
    const singleActiveFilterKey = getSingleActiveFilterKey(activeFilters);
    const siteMap = [];
    const countryMap = [];
    const orgMap = [];
    const ownerMap = [];

    assetGroupsData.forEach((group) => {
        if (group?.sites?.length > 0) {
            group.sites.forEach(s => {
                addSite(s, siteMap);
                addCountry(s, countryMap);
            });
        }

        const orgData = group?.organizationData;
        if (orgData) {
            addOrganisation(orgData, orgMap);
        }

        const owner = group?.fleetData?.owner;
        if (owner) {
            addOwner(owner, ownerMap);
        }
    });

    return {
        allFleetSites: createGroupforSites(allFleets, siteMap, assetGroupsData, singleActiveFilterKey),
        allFleetCountries: createGroupforCountry(allFleets, countryMap, assetGroupsData, singleActiveFilterKey),
        allFleetOrganizations: createGroupforOrganisation(allFleets, orgMap, assetGroupsData, singleActiveFilterKey),
        allFleetOwners: createGroupforOwner(allFleets, ownerMap, assetGroupsData, singleActiveFilterKey)
    };
};

export const handleScrollTopFleetList = () => {
    const el = document.querySelector('.fleets-list');
    if (el && typeof el.scrollTo === 'function') {
        try {
            el.scrollTo({ top: 0, behavior: 'instant' });
        } catch (e) {
            // Log the error and fallback for browsers that don't support the options
            // object or the requested behavior value.
            // eslint-disable-next-line no-console
            console.warn('handleScrollTopFleetList: scrollTo with options failed, falling back to scrollTop.', e);
            el.scrollTop = 0;
        }
    } else if (el) {
        el.scrollTop = 0;
    }
};

